// src/pages/TherapistPage/CaseHistory/MeasuresPanel.js
//
// The score sheet: what the recordings measured, and what the therapist makes
// of it.
//
// Two rules shape this screen.
//
// **Rate, regularity and accuracy sit in separate columns and are never added
// together.** The supervising therapist asked for exactly that: in cerebral
// palsy a child can produce an acceptable number of syllables per second with a
// rhythm and precision that are plainly disordered, and one combined figure
// would hide the finding.
//
// **A number without a reference is shown as a number.** Where no norm exists —
// /ta/, /ka/ and /pa-ta-ka/ today, or any child below five for interpretation —
// the measurement appears with the reason there is no verdict. A guessed
// verdict in a case file is worse than an absent one, because the reader cannot
// tell them apart.
import React, { useCallback, useEffect, useState } from 'react';
import WordAnnotation from './WordAnnotation';
import {
    TASK_KIND, TASK_LABEL, VERDICT_LABEL, analyseSession, ddkAudioPath, fetchAudio,
    getNorms, getScoreSheet, measuresError, saveDdkMeasures, saveVoiceMeasures, voiceAudioPath,
} from './measuresApi';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function Play({ path, label }) {
    const [url, setUrl] = useState(null);
    const [state, setState] = useState('idle');

    const load = async () => {
        if (url) return;
        setState('loading');
        try {
            setUrl(await fetchAudio(path));
            setState('ready');
        } catch (err) {
            setState('unavailable');
        }
    };

    useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

    if (state === 'unavailable') return <span className="cf-hint">no audio</span>;
    if (url) return <audio controls src={url} className="cf-audio" />;
    return (
        <button type="button" className="btn btn-sm btn-secondary" onClick={load} disabled={state === 'loading'}>
            {state === 'loading' ? '…' : '▶ Listen'}
        </button>
    );
}

function Verdict({ norm }) {
    if (!norm) return null;
    if (norm.verdict) {
        const expected = norm.expected || {};
        const range = expected.low != null
            ? `${expected.low}–${expected.high}/s`
            : expected.mean_sec != null ? `mean ${expected.mean_sec}s` : '';
        return (
            <span className={`cf-verdict ${norm.verdict}`} title={norm.source || ''}>
                {VERDICT_LABEL[norm.verdict] || norm.verdict}
                {range ? <em> ({range})</em> : null}
            </span>
        );
    }
    // No verdict is a finding of its own, so it is spelled out rather than left blank.
    return <span className="cf-verdict none" title={norm.source || ''}>— {norm.reason}</span>;
}

/** One oral motor examination, in full.
 *
 * The panel used to print two lists — the structures marked atypical and the
 * postures not achieved — and nothing else, although the response has always
 * carried every answer. Read through its exceptions the examination cannot
 * answer the question it exists to answer: a child whose structures are normal
 * and who *achieved all ten postures*, yet still says /t/ for /k/, has a
 * phonological disorder rather than a weak tongue — they demonstrated the
 * movement. That is read off the word "achieved", and "not achieved: none" does
 * not say it. `none, none` reads as silence; `10 achieved` is evidence.
 */
function OralMotorRecord({ record, label }) {
    const [showDevice, setShowDevice] = useState(false);

    const summary = (counts, order) => order
        .filter((o) => counts[o.key])
        .map((o) => `${counts[o.key]} ${o.label}`)
        .join(' · ');

    const table = (title, rows, counts, order) => (
        <div className="cf-om-group">
            <div className="cf-om-head">
                <span>{title}</span>
                <span className="cf-hint">{summary(counts, order) || 'not answered'}</span>
            </div>
            <table className="cf-om">
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key} className={row.value === 'atypical' || row.value === 'not_achieved'
                            ? 'flagged' : ''}>
                            <td>
                                {row.label}
                                {row.expected ? <em>{row.expected}</em> : null}
                            </td>
                            <td className="cf-om-value">
                                {row.value_label || <span className="cf-hint">not answered</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="cf-om-record">
            <div className="cf-hint">
                {label || `session ${record.capture_session_id}`}
                {record.protocol_version ? ` · protocol ${record.protocol_version}` : ''}
            </div>

            {record.structures.length > 0
                && table('Structures at rest', record.structures, record.structure_counts, STRUCTURE_ORDER)}
            {record.postures.length > 0
                && table('Held postures', record.postures, record.posture_counts, POSTURE_ORDER)}

            {record.notes ? (
                <div className="cf-om-notes">
                    <span className="cf-hint">Examiner&rsquo;s notes</span>
                    <div>{record.notes}</div>
                </div>
            ) : null}

            {/* Folded away, and labelled for what it is. These are blendshape
                scores divided by thresholds nobody validated clinically: no unit,
                no norm, not comparable between postures or between sittings. A
                cue the child's bar was drawn from, not a measurement. */}
            {Object.keys(record.measures || {}).length > 0 && (
                <div className="cf-om-device">
                    <button type="button" className="cf-linkish" onClick={() => setShowDevice((v) => !v)}>
                        {showDevice ? '▾' : '▸'} What the camera read — a device cue, not a measurement
                    </button>
                    {showDevice && (
                        <div className="cf-hint cf-om-device-body">
                            {record.detector?.library ? (
                                <div>detector {record.detector.library}
                                    {record.detector.model ? ` · model ${record.detector.model}` : ''}</div>
                            ) : (
                                <div>The detector version was not recorded for this sitting.</div>
                            )}
                            {Object.entries(record.measures).map(([key, value]) => (
                                <div key={key}>
                                    {key}: {typeof value === 'object' && value !== null
                                        ? Object.entries(value).map(([k, v]) => `${k} ${v}`).join(', ')
                                        : String(value)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const STRUCTURE_ORDER = [
    { key: 'normal', label: 'normal' },
    { key: 'atypical', label: 'atypical' },
    { key: 'not_seen', label: 'not observed' },
    { key: 'unanswered', label: 'not answered' },
];
const POSTURE_ORDER = [
    { key: 'achieved', label: 'achieved' },
    { key: 'partial', label: 'partial' },
    { key: 'not_achieved', label: 'not achieved' },
    { key: 'not_attempted', label: 'not attempted' },
    { key: 'unanswered', label: 'not answered' },
];

export default function MeasuresPanel({ caseId }) {
    const [sheet, setSheet] = useState(null);
    const [norms, setNorms] = useState(null);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [edits, setEdits] = useState({});
    const [trialEdits, setTrialEdits] = useState({});
    const [annotating, setAnnotating] = useState(null);

    const load = useCallback(() => {
        setError('');
        return Promise.all([getScoreSheet(caseId), getNorms()])
            .then(([s, n]) => {
                setSheet(s.data); setNorms(n.data); setEdits({}); setTrialEdits({});
            })
            .catch((err) => setError(measuresError(err, 'Could not load the score sheet.')));
    }, [caseId]);

    useEffect(() => { load(); }, [load]);

    if (error) return <div className="cf-note red">{error}</div>;
    if (!sheet) return <div className="cf-empty">Loading…</div>;

    // Named from the sheet's own list. A raw database id on screen invites the
    // question "where are 2 and 3?" — they belong to other children.
    const sittings = sheet.sittings?.length
        ? sheet.sittings
        : [...new Set(sheet.ddk.map((t) => t.capture_session_id))]
            .map((id, i) => ({ id, number: i + 1, date: null, status: '' }));
    // Day before month, written out rather than left to the browser's locale:
    // the same case file is read on a clinic desktop and a field tablet, and
    // "Sep 28" against "28 Sep" is the kind of difference that makes two readers
    // disagree about which sitting came first.
    const sittingLabel = (seat) => {
        if (!seat) return '';
        let when = '';
        if (seat.date) {
            const d = new Date(seat.date);
            if (!Number.isNaN(d.getTime())) when = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
        }
        return `Sitting ${seat.number}${when ? ` · ${when}` : ''}`;
    };

    const runAnalysis = async (sessionId) => {
        setBusy(true); setMessage('');
        try {
            const { data } = await analyseSession(sessionId);
            await load();
            const skipped = (data.skipped || []).length;
            setMessage(`Measured ${data.measured.length} recording(s)`
                + (skipped ? `, skipped ${skipped} — see the rows for why.` : '.'));
        } catch (err) {
            setError(measuresError(err, 'Could not measure this session.'));
        } finally { setBusy(false); }
    };

    // Keyed per attempt: the two tries at one sound are edited separately.
    const editKey = (task) => `${task.task}#${task.trial}`;
    const editOf = (task) => edits[editKey(task)] || {};
    const setEdit = (task, field, value) => setEdits((prev) => ({
        ...prev, [editKey(task)]: { ...(prev[editKey(task)] || {}), [field]: value },
    }));

    const confirmTask = async (task) => {
        const edit = editOf(task);
        setBusy(true); setMessage(''); setError('');
        try {
            const body = { trial: task.trial, confirmed: true };
            if (edit.syllable_count !== undefined && edit.syllable_count !== '') {
                body.syllable_count = Number(edit.syllable_count);
            }
            if (edit.regularity) body.regularity = edit.regularity;
            if (edit.quality) body.quality = edit.quality;
            if (edit.examiner_note !== undefined) body.examiner_note = edit.examiner_note;
            await saveDdkMeasures(task.capture_session_id, task.task, body);
            await load();
            setMessage(`${TASK_LABEL[task.task]} try ${task.trial} confirmed.`);
        } catch (err) {
            setError(measuresError(err, 'Could not save that.'));
        } finally { setBusy(false); }
    };

    /** Mark one attempt unusable, or usable again.
     *
     * A reason is required when dropping it: a sample removed without one
     * leaves a reader unable to tell a spoiled recording from a hidden one.
     */
    const dropTrial = async (task) => {
        const dropping = task.usable !== false;
        let reason = null;
        if (dropping) {
            reason = window.prompt('Why can this sample not be used?');
            if (!reason || !reason.trim()) return;
        }
        setBusy(true); setError(''); setMessage('');
        try {
            await saveDdkMeasures(task.capture_session_id, task.task, {
                trial: task.trial,
                usable: !dropping,
                unusable_reason: dropping ? reason.trim() : null,
                confirmed: true,
            });
            await load();
            setMessage(dropping
                ? `${TASK_LABEL[task.task]} try ${task.trial} left out of the figure.`
                : `${TASK_LABEL[task.task]} try ${task.trial} back in the figure.`);
        } catch (err) {
            setError(measuresError(err, 'Could not save that.'));
        } finally { setBusy(false); }
    };

    const confirmTrial = async (trial) => {
        const seconds = trialEdits[trial.id];
        setBusy(true); setError('');
        try {
            const body = { confirmed: true };
            if (seconds !== undefined && seconds !== '') body.phonation_ms = Math.round(Number(seconds) * 1000);
            await saveVoiceMeasures(trial.id, body);
            await load();
            setMessage('Phonation time confirmed.');
        } catch (err) {
            setError(measuresError(err, 'Could not save that.'));
        } finally { setBusy(false); }
    };

    const accuracyScale = norms?.ddk?.accuracy_scale || [
        { key: 'normal', score: 0, en: 'Normal' },
        { key: 'mild', score: 1, en: 'Mildly imprecise' },
        { key: 'moderate', score: 2, en: 'Moderately imprecise' },
        { key: 'severe', score: 3, en: 'Severely imprecise' },
    ];

    if (annotating) {
        return (
            <WordAnnotation
                sessionId={annotating}
                onBack={() => { setAnnotating(null); load(); }}
            />
        );
    }

    return (
        <div>
            {message && <div className="cf-note green">{message}</div>}

            {sheet.norms_provisional && (
                <div className="cf-note amber">
                    <b>Reference values are provisional.</b> {sheet.norms_status}
                </div>
            )}

            <div className="cf-bar">
                <span className="cf-hint">
                    {sheet.child_name}
                    {sheet.age_months != null
                        ? ` · ${Math.floor(sheet.age_months / 12)}y ${sheet.age_months % 12}m`
                        : ''}
                </span>
                {sittings.map((seat) => (
                    <button
                        key={seat.id} type="button" className="btn btn-sm btn-secondary"
                        disabled={busy} onClick={() => runAnalysis(seat.id)}
                    >
                        Measure {sittingLabel(seat).toLowerCase()}
                        {seat.status === 'complete' ? ' (closed)' : ''}
                    </button>
                ))}
            </div>

            {/* ── Diadochokinetic rate ─────────────────────────────────── */}
            <div className="cf-section">
                <h4>Diadochokinetic rate</h4>
                {sheet.ddk.length === 0 ? (
                    <div className="cf-empty">Nothing recorded for this block yet.</div>
                ) : (
                    <table className="cf-measures">
                        <thead>
                            <tr>
                                <th>Task</th>
                                <th>Syllables</th>
                                <th>Rate</th>
                                <th>Time for 20</th>
                                <th>Regularity</th>
                                <th>Accuracy</th>
                                <th>Against norms</th>
                                <th>Audio</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sheet.ddk.flatMap((taskRow) => {
                                if (taskRow.method === 'not_attempted') {
                                    return [(
                                        <tr key={taskRow.task} className="cf-not-attempted">
                                            <td><b>{TASK_LABEL[taskRow.task]}</b> <span className="cf-hint">{TASK_KIND[taskRow.task]}</span></td>
                                            <td colSpan="8" className="cf-hint">
                                                Not attempted — {taskRow.na_reason}. This is a finding, not a
                                                missing measurement, so it is not scored.
                                            </td>
                                        </tr>
                                    )];
                                }

                                // The task's combined figure, then its attempts
                                // underneath. The verdict belongs to the task:
                                // judging one sample would judge the cough too.
                                const summary = (
                                    <tr key={taskRow.task} className="cf-task-summary">
                                        <td>
                                            <b>{TASK_LABEL[taskRow.task]}</b>{' '}
                                            <span className="cf-hint">{TASK_KIND[taskRow.task]}</span>
                                            <div className="cf-hint">
                                                {taskRow.method === 'average'
                                                    ? `averaged over tries ${taskRow.trials_used.join(' and ')}`
                                                    : taskRow.method === 'single'
                                                        ? `try ${taskRow.trials_used[0]} only`
                                                        : 'no usable sample'}
                                            </div>
                                        </td>
                                        <td className="cf-hint">
                                            {taskRow.trials.length} of {taskRow.trials_expected} tries
                                        </td>
                                        <td><b>{taskRow.rate_per_sec != null ? `${taskRow.rate_per_sec}/s` : '—'}</b></td>
                                        <td>{taskRow.seconds_for_20 != null ? `${taskRow.seconds_for_20}s` : '—'}</td>
                                        <td>
                                            {taskRow.regularity || '—'}
                                            {taskRow.regularity_cv != null && (
                                                <div className="cf-hint">cv {taskRow.regularity_cv}</div>
                                            )}
                                        </td>
                                        <td />
                                        <td><Verdict norm={taskRow.norm} /></td>
                                        <td colSpan="2" />
                                    </tr>
                                );

                                return [summary, ...taskRow.trials.map((task) => {
                                const edit = editOf(task);
                                return (
                                    <tr key={task.id} className={task.confirmed ? 'cf-confirmed' : ''}>
                                        <td className="cf-attempt-cell">
                                            try {task.trial}
                                            {task.usable === false && (
                                                <div className="cf-warn">
                                                    not used — {task.unusable_reason}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number" min="0" className="cf-num"
                                                value={edit.syllable_count ?? (task.syllable_count ?? '')}
                                                onChange={(e) => setEdit(task, 'syllable_count', e.target.value)}
                                            />
                                            {task.syllable_count_source === 'examiner' && (
                                                <div className="cf-hint">machine said {task.syllable_count_auto}</div>
                                            )}
                                        </td>
                                        <td>
                                            {task.rate_per_sec != null ? `${task.rate_per_sec}/s` : '—'}
                                            {/* The duration is this rate's denominator, so a
                                                recording missing a second of audio produces a
                                                figure that looks ordinary and is not. */}
                                            {task.edits?.length > 0 && (
                                                <details className="cf-trail">
                                                    <summary>{task.edits.length} edit(s)</summary>
                                                    {task.edits.map((e) => (
                                                        <div key={e.id}>
                                                            {e.field}: {e.old_value ?? '—'} → {e.new_value ?? '—'}
                                                            {' · '}{e.changed_by_name || 'unknown'}
                                                        </div>
                                                    ))}
                                                </details>
                                            )}
                                            {task.recording_short && (
                                                <div className="cf-warn">
                                                    recording {task.missing_ms} ms short of{' '}
                                                    {task.expected_duration_ms} ms — audio may be
                                                    missing; consider recording this task again
                                                </div>
                                            )}
                                        </td>
                                        <td>{task.seconds_for_20 != null ? `${task.seconds_for_20}s` : '—'}</td>
                                        <td>
                                            <select
                                                value={edit.regularity ?? (task.regularity || '')}
                                                onChange={(e) => setEdit(task, 'regularity', e.target.value)}
                                            >
                                                <option value="">—</option>
                                                <option value="regular">Regular</option>
                                                <option value="irregular">Irregular</option>
                                            </select>
                                            {task.regularity_cv != null && (
                                                <div className="cf-hint">cv {task.regularity_cv}</div>
                                            )}
                                            {/* The machine gives a coefficient; only a person can
                                                say why — "paused twice for breath" is the finding,
                                                not the number. */}
                                            <input
                                                type="text" className="cf-note-input"
                                                placeholder="note (optional)"
                                                value={edit.examiner_note ?? (task.examiner_note || '')}
                                                onChange={(e) => setEdit(task, 'examiner_note', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                value={edit.quality ?? (task.quality || '')}
                                                onChange={(e) => setEdit(task, 'quality', e.target.value)}
                                            >
                                                <option value="">—</option>
                                                {accuracyScale.map((grade) => (
                                                    <option key={grade.key} value={grade.key}>
                                                        {grade.score} — {grade.en}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <button
                                                type="button" className="btn btn-sm btn-secondary"
                                                disabled={busy}
                                                onClick={() => dropTrial(task)}
                                            >
                                                {task.usable === false ? 'Use it' : "Can't use"}
                                            </button>
                                        </td>
                                        <td>{task.has_audio ? <Play path={ddkAudioPath(task.id)} /> : <span className="cf-hint">—</span>}</td>
                                        <td>
                                            <button
                                                type="button" className="btn btn-sm btn-primary"
                                                disabled={busy} onClick={() => confirmTask(task)}
                                            >
                                                {task.confirmed ? 'Re-confirm' : 'Confirm'}
                                            </button>
                                            {task.confirmed && (
                                                <div className="cf-hint">by {task.confirmed_by_name}</div>
                                            )}
                                        </td>
                                    </tr>
                                );
                                })];
                            })}
                        </tbody>
                    </table>
                )}
                <p className="cf-hint">
                    Two attempts at each sound, averaged when both are usable. Mark a sample
                    spoiled by a cough or a false start as unusable and the other stands alone —
                    a bad recording is not evidence of a slow child.
                </p>
                <p className="cf-hint">
                    Rate, regularity and accuracy are reported separately on purpose: an acceptable
                    rate can sit beside a disordered rhythm, and a single figure would hide it.
                    Both the syllables-per-second and the time-for-20-repetitions figures come from
                    the same raw count, so either can be compared with older norm tables.
                </p>
            </div>

            {/* ── Maximum phonation time ───────────────────────────────── */}
            <div className="cf-section">
                <h4>Maximum phonation time</h4>
                {!sheet.mpt || sheet.mpt.trials.length === 0 ? (
                    <div className="cf-empty">No sustained vowel recorded yet.</div>
                ) : (
                    <>
                        <table className="cf-measures">
                            <thead>
                                <tr>
                                    <th>Trial</th>
                                    <th>Phonation</th>
                                    <th>Recording</th>
                                    <th>Audio</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {sheet.mpt.trials.map((trial) => (
                                    <tr
                                        key={trial.id}
                                        className={trial.phonation_sec === sheet.mpt.aggregate_sec ? 'cf-best' : ''}
                                    >
                                        <td>
                                            {trial.trial}
                                            {trial.phonation_sec === sheet.mpt.aggregate_sec && (
                                                <span className="cf-hint"> taken</span>
                                            )}
                                        </td>
                                        <td>
                                            <input
                                                type="number" step="0.1" min="0" className="cf-num"
                                                value={trialEdits[trial.id] ?? (trial.phonation_sec ?? '')}
                                                onChange={(e) => setTrialEdits((prev) => ({
                                                    ...prev, [trial.id]: e.target.value,
                                                }))}
                                            />{' '}s
                                            {trial.phonation_source === 'examiner' && trial.phonation_ms_auto != null && (
                                                <div className="cf-hint">
                                                    machine said {(trial.phonation_ms_auto / 1000).toFixed(2)}s
                                                </div>
                                            )}
                                            {/* Still sounding when the file ended: a floor, not a
                                                limit. Reading it as the child's limit would
                                                understate a child who was doing well. */}
                                            {trial.ran_to_end && (
                                                <div className="cf-warn">
                                                    at least this long — the sound had not finished
                                                    when the recording ended
                                                </div>
                                            )}
                                        </td>
                                        <td className="cf-hint">
                                            {trial.duration_ms ? `${(trial.duration_ms / 1000).toFixed(1)}s long` : '—'}
                                        </td>
                                        <td><Play path={voiceAudioPath(trial.id)} /></td>
                                        <td>
                                            <button
                                                type="button" className="btn btn-sm btn-primary" disabled={busy}
                                                onClick={() => confirmTrial(trial)}
                                            >
                                                {trial.confirmed ? 'Re-confirm' : 'Confirm'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {sheet.mpt.other_vowels?.length > 0 && (
                            <div className="cf-note blue">
                                <b>Other vowels recorded:</b>{' '}
                                {sheet.mpt.other_vowels
                                    .map((t) => `/${t.vowel}/ ${t.phonation_sec ?? '—'}s`)
                                    .join(' · ')}
                                <div className="cf-hint">
                                    Listed, not judged: the reference values are for /a/, so comparing
                                    an /i/ or /u/ recording to them would compare two different tasks.
                                </div>
                            </div>
                        )}
                        <div className="cf-grid">
                            <div>
                                <span className="cf-hint">Taken ({sheet.mpt.aggregate_method})</span>
                                <div className="cf-big">{sheet.mpt.aggregate_sec ?? '—'}s</div>
                            </div>
                            <div>
                                <span className="cf-hint">Best / average</span>
                                <div>{sheet.mpt.best_sec ?? '—'}s / {sheet.mpt.average_sec ?? '—'}s</div>
                            </div>
                            <div>
                                <span className="cf-hint">Against norms</span>
                                <div><Verdict norm={sheet.mpt.norm} /></div>
                            </div>
                        </div>
                        <p className="cf-hint">
                            Phonation is the length of the sound, not of the recording — the breath
                            before it and the pause after it are not counted, because the reference
                            values are about the sound.
                        </p>
                    </>
                )}
            </div>

            {/* ── Word list ────────────────────────────────────────────── */}
            <div className="cf-section">
                <h4>Word list</h4>
                <div className="cf-grid">
                    <div>
                        <span className="cf-hint">PCC (checked words)</span>
                        <div className="cf-big">
                            {sheet.word_list?.pcc_verified != null
                                ? `${sheet.word_list.pcc_verified}%`
                                : 'not yet'}
                        </div>
                        <span className="cf-hint">{sheet.word_list?.words_verified || 0} words checked</span>
                    </div>
                    <div>
                        <span className="cf-hint">Provisional, from recognition</span>
                        <div>
                            {sheet.word_list?.pcc_provisional != null
                                ? `${sheet.word_list.pcc_provisional}%`
                                : '—'}
                        </div>
                        <span className="cf-hint">
                            {sheet.word_list?.words_provisional || 0} words nobody has corrected
                        </span>
                    </div>
                    <div>
                        {sittings.map((seat) => (
                            <button
                                key={seat.id} type="button" className="btn btn-sm btn-secondary"
                                onClick={() => setAnnotating(seat.id)}
                            >
                                Check words · {sittingLabel(seat).toLowerCase()}
                            </button>
                        ))}
                    </div>
                </div>
                {sheet.word_list?.note && <p className="cf-hint">{sheet.word_list.note}</p>}

                {/* PCC compares IPA; the label is the judgement of whoever listened.
                    When they disagree the usual cause is a wrong reference rather than
                    a wrong child — and the screen used to print a figure that quietly
                    contradicted the therapist without ever saying so. */}
                {sheet.word_list?.conflict && (
                    <div className="cf-note amber">
                        <b>This figure disagrees with your own labels.</b>
                        <div style={{ marginTop: 4 }}>
                            {sheet.word_list.conflict.marked_correct_with_errors > 0 && (
                                <div>
                                    {sheet.word_list.conflict.marked_correct_with_errors} word(s) you
                                    marked <b>correct</b> count{' '}
                                    {sheet.word_list.conflict.errors_in_those_words} consonant
                                    error(s) against the stored reference IPA.
                                </div>
                            )}
                            {sheet.word_list.conflict.marked_incorrect_but_clean > 0 && (
                                <div>
                                    {sheet.word_list.conflict.marked_incorrect_but_clean} word(s) you
                                    marked <b>incorrect</b> are counted as fully correct.
                                </div>
                            )}
                        </div>
                        <div className="cf-hint" style={{ marginTop: 4 }}>
                            {sheet.word_list.conflict.examples.join(' · ')}
                        </div>
                        <div className="cf-hint" style={{ marginTop: 4 }}>
                            Check the reference IPA for those words before reading the figure as a
                            finding about the child. `scripts/audit_reference_ipa.py` lists the
                            suspect entries.
                        </div>
                    </div>
                )}

                {(sheet.word_list?.processes || []).length > 0 && (
                    <table className="cf-measures">
                        <thead>
                            <tr>
                                <th>Pattern</th><th>Times seen</th><th>In words</th>
                                <th>Share of words</th><th>Examples</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sheet.word_list.processes.map((process) => (
                                <tr key={process.key}>
                                    <td><b>{process.key.replace(/_/g, ' ')}</b></td>
                                    {/* Two different counts, kept apart: three fronted velars
                                        in one word are three events and one word, and the
                                        share is about words. */}
                                    <td>{process.count}</td>
                                    <td>
                                        {process.words != null
                                            ? `${process.words} of ${process.words_total}`
                                            : '—'}
                                    </td>
                                    <td>{process.share != null ? `${Math.round(process.share * 100)}%` : '—'}</td>
                                    <td className="cf-hint">{process.examples.join(' · ')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Oral motor ───────────────────────────────────────────── */}
            {sheet.oral_motor.length > 0 && (
                <div className="cf-section">
                    <h4>Oral motor examination</h4>
                    {sheet.oral_motor.map((record) => (
                        <OralMotorRecord
                            key={record.id} record={record}
                            label={sittingLabel(sittings.find((seat) => seat.id === record.capture_session_id))}
                        />
                    ))}
                    <p className="cf-hint">
                        The examiner's judgement is the finding. No video was stored.
                    </p>
                </div>
            )}

            {/* ── The child's own history ──────────────────────────────── */}
            {(Object.keys(sheet.trend?.ddk || {}).length > 0 || sheet.trend?.mpt?.length > 0) && (
                <div className="cf-section">
                    <h4>Compared with this child's earlier sittings</h4>
                    <p className="cf-hint">
                        For a child with cerebral palsy this is the comparison that matters most —
                        typical norms above are context, not the measure. Only figures a therapist
                        confirmed appear here.
                    </p>
                    <table className="cf-measures">
                        <thead>
                            <tr><th>Measure</th><th>Earlier sittings</th><th>Now</th></tr>
                        </thead>
                        <tbody>
                            {Object.entries(sheet.trend.ddk).map(([task, points]) => {
                                const current = sheet.ddk.find((t) => t.task === task);
                                return (
                                    <tr key={task}>
                                        <td><b>{TASK_LABEL[task] || task}</b></td>
                                        <td className="cf-hint">
                                            {points.map((p) => (
                                                <span key={`${p.case_history_id}-${p.sitting?.id}`} className="cf-trend-point">
                                                    {p.rate_per_sec}/s
                                                    <em>{sittingLabel(p.sitting)}</em>
                                                </span>
                                            ))}
                                        </td>
                                        <td>
                                            {current?.confirmed && current.rate_per_sec != null
                                                ? `${current.rate_per_sec}/s`
                                                : <span className="cf-hint">not confirmed yet</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                            {sheet.trend.mpt?.length > 0 && (
                                <tr>
                                    <td><b>Phonation /a/</b></td>
                                    <td className="cf-hint">
                                        {sheet.trend.mpt.map((p) => (
                                            <span key={`${p.case_history_id}-${p.sitting?.id}`} className="cf-trend-point">
                                                {p.rate_per_sec}s
                                                <em>{sittingLabel(p.sitting)}</em>
                                            </span>
                                        ))}
                                    </td>
                                    <td>
                                        {sheet.mpt?.aggregate_sec != null
                                            ? `${sheet.mpt.aggregate_sec}s`
                                            : <span className="cf-hint">—</span>}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Suggested signals ────────────────────────────────────── */}
            <div className="cf-section">
                <h4>What these measurements point at</h4>
                {sheet.suggested_signals.length === 0 ? (
                    <div className="cf-empty">
                        Nothing yet. Suggestions appear once a measurement has been confirmed and a
                        reference exists to compare it against.
                    </div>
                ) : (
                    <>
                        <ul className="cf-suggestions">
                            {sheet.suggested_signals.map((item) => (
                                <li key={item.signal}>
                                    <b>{item.label || item.signal}</b>
                                    <div className="cf-hint">{item.reason}</div>
                                </li>
                            ))}
                        </ul>
                        <p className="cf-hint">
                            Suggestions only. Nothing here has been written to the case file — mark
                            what you agree with on the <b>Clinical</b> tab, where it is recorded in
                            your name.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
